import { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import Lottie from 'lottie-react';

import LoadingAnimation from '../../../../../Animation/NewLoading.json';

import { useLanguage } from '../../../../../Context/LanguageContext';
import { useTheme } from '../../../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../../../Styles/theme';
import { formatPhoneNumber } from '../../../../../Utils/phoneUtils';

import FetchModule from '../../../../Share/Network/FetchModule';
import SortingModule from '../../../../Share/Normal/SortingModule';

import SearchInput from '../../../../../View/Message/Model/SearchInput';
import SelectGenderBox from '../../../../../Component/MemberComponent/MemberDetail/SelectGenderBox';
import UserProfile from '../../../../../Component/Normal/UserProfile';
import SelectHoldingStateBox from '../../../../../Component/MemberComponent/MemberDetail/SelectHoldingStateBox';
import SelectStatusBox from '../../../../../Component/MemberComponent/MemberDetail/SelectStatusBox';
import SelectMembershipBox from '../../../../../Component/MemberComponent/MemberDetail/SelectMembershipBox';
import CustomScrollbar from '../../../../Share/Normal/CustomScrollbar';

import { ReactComponent as SortingButtonSvg } from '../../../../../image/SortingButton.svg';
import DefaultProfile from '../../../../../image/DefaultProfile.png';
import { ReactComponent as CheckSvg } from '../../../../../image/Check2.svg';
import { ReactComponent as PlusSvg } from '../../../../../image/Plus.svg';

// 포인트 일괄 지급 — 회원 선택 패널 //
const BulkPointMemberSelectView = forwardRef((props, ref) => {
  const { startDate } = props;
  const { t, languageMode } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [memberships, setMemberships] = useState([]);
  const [memberList, setMemberList] = useState([]);
  const [members, setMembers] = useState([]);
  const [originalSortedMembers, setOriginalSortedMembers] = useState([]);
  const [sortedMembers, setSortedMembers] = useState([]);
  const [notPaginationSortedMembers, setNotPaginationSortedMembers] = useState([]);
  const [searchValue, setSearchValue] = useState('');

  const [isClickedValidSorting, setIsClickedValidSorting] = useState(false);
  const [isClickedExpectedSorting, setIsClickedExpectedSorting] = useState(false);

  const [isClickedMaleSorting, setIsClickedMaleSorting] = useState(false);
  const [isClickedFemaleSorting, setIsClickedFemaleSorting] = useState(false);

  const [isClickedHoldingSorting, setIsClickedHoldingSorting] = useState(false);
  const [isClickedMaintainSorting, setIsClickedMaintainSorting] = useState(false);

  const [selectedMemberships, setSelectedMemberships] = useState([]);

  const [startSort, setStartSort] = useState('normal');
  const [endSort, setEndSort] = useState('desc');

  const [isClickedAllMemberSelect, setIsClickedAllMemberSelect] = useState(false);
  const [nowPage, setNowPage] = useState(0);
  const [totalPage, setTotalPage] = useState(1);

  const [isLoading, setIsLoading] = useState(true);

  const SelectedScrollRef = useRef(null);

  // 부모에 선택된 memberIds 조회 메서드 노출 //
  useImperativeHandle(ref, () => ({
    getSelectedMemberIds: () => originalSortedMembers.filter((data) => data.isClicked).map((data) => data.memberId)
  }));

  // 회원 + 회원권 카탈로그 조회 //
  useEffect(() => {
    const fetchModule = new FetchModule();

    (
      async () => {
        const responseData = await fetchModule.getDataVersion2('member/roster', 'GET');

        if (responseData.status === 200) {
          // 백엔드 gender 'M' / 'F' → 'male' / 'female' 정규화 (필터·표시 로직 호환) //
          const normalizedMembers = responseData.data.members.map((data) => ({
            ...data,
            gender: data.gender === 'M' ? 'male' : data.gender === 'F' ? 'female' : data.gender,
            isClicked: false
          }));

          setMemberships(responseData.data.memberships);
          setMemberList(normalizedMembers);
        }

        setIsLoading(false);
      }
    )();
  }, [])

  // memberList → members / originalSortedMembers 초기 정렬 //
  useEffect(() => {
    let newMembers = memberList.slice();

    newMembers = SortingModule.dataSort(newMembers, 'endDate', 'desc', 'endDate');

    setMembers(newMembers);
    setOriginalSortedMembers(newMembers);
  }, [memberList])

  // 필터/정렬/검색/페이지네이션 처리 //
  useEffect(() => {
    if (members.length === 0) {
      setSortedMembers([]);
      return;
    }

    let newMembers = [];

    let filterMembers = [];
    let standardDate = moment(startDate || moment().format('YYYY.MM.DD'), 'YYYY.MM.DD');

    for (let i = 0; i < members.length; i++) {
      if (moment(members[i].endDate).isAfter(standardDate)) {
        let isHolding = false;

        if (Array.isArray(members[i].holdings)) {
          for (let j = 0; j < members[i].holdings.length; j++) {
            if (moment(members[i].holdings[j].startDate).isBefore(standardDate)
              && moment(members[i].holdings[j].endDate).isAfter(standardDate)) {
              isHolding = true;
              break;
            }
          }
        }

        members[i].isHolding = isHolding;

        filterMembers.push(members[i]);
      }
    }

    // 유효, 예정 상태 추출 //
    let statusMembers = [];

    if ((isClickedValidSorting && isClickedExpectedSorting)
      || (!isClickedValidSorting && !isClickedExpectedSorting)) {
      statusMembers = filterMembers;
    }
    else if (isClickedValidSorting) {
      filterMembers.map((data) => {
        if (moment(data.startDate).isBefore(standardDate)
          && moment(data.endDate).isAfter(standardDate)) statusMembers.push(data);
      })
    }
    else if (isClickedExpectedSorting) {
      filterMembers.map((data) => {
        if (moment(data.startDate).isAfter(standardDate)
          && moment(data.endDate).isAfter(standardDate)) statusMembers.push(data);
      })
    }

    // 성별 추출 //
    let genderCheckMembers = [];

    if ((isClickedFemaleSorting && isClickedMaleSorting)
      || (!isClickedFemaleSorting && !isClickedMaleSorting)) {
      genderCheckMembers = statusMembers;
    }
    else if (isClickedFemaleSorting) {
      statusMembers.map((data) => {
        if (data.gender === 'female') genderCheckMembers.push(data);
      })
    }
    else if (isClickedMaleSorting) {
      statusMembers.map((data) => {
        if (data.gender === 'male') genderCheckMembers.push(data);
      })
    }

    // 홀딩 추출 //
    let holdingCheckMembers = [];

    if ((isClickedHoldingSorting && isClickedMaintainSorting)
      || (!isClickedHoldingSorting && !isClickedMaintainSorting)) {
      holdingCheckMembers = genderCheckMembers;
    }
    else if (isClickedHoldingSorting) {
      genderCheckMembers.map((data) => {
        if (data.isHolding) holdingCheckMembers.push(data);
      })
    }
    else if (isClickedMaintainSorting) {
      genderCheckMembers.map((data) => {
        if (!data.isHolding) holdingCheckMembers.push(data);
      })
    }

    let membershipCheckMembers = [];

    if (selectedMemberships.length === 0) membershipCheckMembers = holdingCheckMembers;
    else {
      holdingCheckMembers.map((data) => {
        for (let i = 0; i < selectedMemberships.length; i++) {
          if (data.membershipName === selectedMemberships[i].membershipName) {
            membershipCheckMembers.push(data);
            break;
          }
        }
      })
    }

    if (startSort === 'asc') membershipCheckMembers = SortingModule.dataSort(membershipCheckMembers, 'startDate', 'asc', 'startDate');
    else if (startSort === 'desc') membershipCheckMembers = SortingModule.dataSort(membershipCheckMembers, 'startDate', 'desc', 'startDate');
    else if (endSort === 'asc') membershipCheckMembers = SortingModule.dataSort(membershipCheckMembers, 'endDate', 'asc', 'endDate');
    else if (endSort === 'desc') membershipCheckMembers = SortingModule.dataSort(membershipCheckMembers, 'endDate', 'desc', 'endDate');

    let nameList = [];
    let phoneList = [];

    for (let i = 0; i < membershipCheckMembers.length; i++) {
      if (membershipCheckMembers[i].name.toLowerCase().includes(searchValue.toLowerCase())) nameList.push(membershipCheckMembers[i]);
      else if (membershipCheckMembers[i].phone.includes(searchValue)) phoneList.push(membershipCheckMembers[i]);
    }

    let totalList = nameList.concat(phoneList);

    if (totalList.length > 10) {
      let finalPage = Math.ceil(totalList.length / 10);

      if (nowPage >= finalPage) setNowPage(finalPage - 1);

      setTotalPage(finalPage);
    }
    else {
      setNowPage(0);
      setTotalPage(1);
    }

    if (totalList.length !== 0) {
      if (totalList.length > 10) newMembers = totalList.slice((nowPage * 10), (nowPage * 10) + 10);
      else newMembers = totalList.slice();
    }

    setSortedMembers(newMembers);
    setNotPaginationSortedMembers(totalList);
    // 필터링 결과의 모든 회원이 체크되어 있으면 전체선택 유지 //
    setIsClickedAllMemberSelect(totalList.length > 0 && totalList.every((data) => data.isClicked));
  }, [
    members,
    searchValue,
    isClickedValidSorting,
    isClickedExpectedSorting,
    isClickedHoldingSorting,
    isClickedMaintainSorting,
    isClickedMaleSorting,
    isClickedFemaleSorting,
    selectedMemberships,
    startSort,
    endSort,
    nowPage,
    startDate
  ])

  // 몇명 선택되었는지 텍스트 //
  function decideSelectedCountText() {
    let count = 0;

    originalSortedMembers.map((data) => {
      if (data.isClicked) count += 1;
    })

    return count + t('memberDetail.pointSaveModal.personSuffix');
  }

  // 필터 초기화 //
  function onClickResetFilter() {
    let newOriginalSortedMembers = originalSortedMembers.slice();

    newOriginalSortedMembers.map((data) => data.isClicked = false);

    setOriginalSortedMembers(newOriginalSortedMembers);
    setNotPaginationSortedMembers(newOriginalSortedMembers);

    setSearchValue('');
    setIsClickedMaleSorting(false);
    setIsClickedFemaleSorting(false);
    setIsClickedAllMemberSelect(false);
    setStartSort('normal');
    setEndSort('desc');
  }

  function onClickStartSort() {
    setEndSort('normal');

    if (startSort === 'normal' || startSort === 'desc') setStartSort('asc');
    else if (startSort === 'asc') setStartSort('desc');
  }

  function onClickEndSort() {
    setStartSort('normal');

    if (endSort === 'normal' || endSort === 'desc') setEndSort('asc');
    else if (endSort === 'asc') setEndSort('desc');
  }

  function onClickAllSelectButton() {
    let newNotPaginationSortedMembers = notPaginationSortedMembers.slice();

    newNotPaginationSortedMembers.map((data) => data.isClicked = !isClickedAllMemberSelect);

    setNotPaginationSortedMembers(newNotPaginationSortedMembers);
    setIsClickedAllMemberSelect(!isClickedAllMemberSelect);
  }

  function onClickPrevButton() {
    if (nowPage !== 0) setNowPage(nowPage - 1);
  }

  function onClickNextButton() {
    if ((nowPage + 1) !== totalPage) setNowPage(nowPage + 1);
  }

  function onClickMemberInfo(data) {
    let newOriginalSortedMembers = originalSortedMembers.slice();
    let index = newOriginalSortedMembers.findIndex(item => item.usageId === data.usageId);

    newOriginalSortedMembers[index].isClicked = !newOriginalSortedMembers[index].isClicked;

    setOriginalSortedMembers(newOriginalSortedMembers);
  }

  function onClickDeleteButton(data) {
    let newOriginalSortedMembers = originalSortedMembers.slice();
    let index = newOriginalSortedMembers.findIndex(item => item.memberId === data.memberId);

    newOriginalSortedMembers[index].isClicked = !newOriginalSortedMembers[index].isClicked;

    setOriginalSortedMembers(newOriginalSortedMembers);
  }

  // 개별 체크 변경에 따른 전체선택 상태 동기화 //
  useEffect(() => {
    setIsClickedAllMemberSelect(notPaginationSortedMembers.length > 0
      && notPaginationSortedMembers.every((data) => data.isClicked));
  }, [originalSortedMembers, notPaginationSortedMembers])

  return (
    <AdditionMemberView>
      <SearchInputWrapper>
        <SearchInput value={searchValue} setValue={setSearchValue} placeholder={t('memberDetail.extensionModal.searchMember')} width={318} />
      </SearchInputWrapper>
      <ResetAndSortingButtonWrapper>
        <SortingButtonWrapper>
          <SelectStatusBox
            isClickedValidSorting={isClickedValidSorting}
            isClickedExpectedSorting={isClickedExpectedSorting}
            setIsClickedValidSorting={setIsClickedValidSorting}
            setIsClickedExpectedSorting={setIsClickedExpectedSorting}
            pointColor={theme.subKey} />

          <SelectGenderBox
            isClickedMaleSorting={isClickedMaleSorting}
            isClickedFemaleSorting={isClickedFemaleSorting}
            setIsClickedMaleSorting={setIsClickedMaleSorting}
            setIsClickedFemaleSorting={setIsClickedFemaleSorting}
            pointColor={theme.subKey} />

          <SelectMembershipBox
            value={selectedMemberships}
            setValue={setSelectedMemberships}
            memberships={memberships}
            pointColor={theme.subKey} />
        </SortingButtonWrapper>
        <FilterResetButton onClick={onClickResetFilter}>
          <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} hover='pointer'>{t('memberDetail.extensionModal.resetFilter')}</Text>
        </FilterResetButton>
      </ResetAndSortingButtonWrapper>
      <TotalMemberBoxWrapper>
        <TotalMemberBox>
          <TotalMemberColumnWrppaer>
            <MemberCheckBox isClicked={isClickedAllMemberSelect} onClick={onClickAllSelectButton} style={{ marginRight: '10px' }}>
              {
                isClickedAllMemberSelect &&
                <CheckIcon />
              }
            </MemberCheckBox>
            <TextWrapper marginRight={10} width={120}>
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} hover='default'>{t('memberDetail.colName')}</Text>
            </TextWrapper>
            <TextWrapper marginRight={10} width={50}>
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} hover='default'>{t('memberDetail.labelGender')}</Text>
            </TextWrapper>
            <TextWrapper marginRight={10}  width={200}>
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} hover='default'>{t('memberDetail.extensionModal.membership')}</Text>
            </TextWrapper>
            <AscDescButtonWrapper marginRight={10} width={75}>
              <TextWrapper width={40}>
                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} hover='default'>{t('memberDetail.extensionModal.startDate')}</Text>
              </TextWrapper>
              <SortingButton $sort={startSort} onClick={() => onClickStartSort()} />
            </AscDescButtonWrapper>
            <AscDescButtonWrapper marginRight={10} width={75}>
              <TextWrapper width={40}>
                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} hover='default'>{t('memberDetail.extensionModal.endDate')}</Text>
              </TextWrapper>
              <SortingButton $sort={endSort} onClick={() => onClickEndSort()} />
            </AscDescButtonWrapper>
          </TotalMemberColumnWrppaer>
          <TotalMemberListWrapper>
            {
              isLoading &&
              <LoadingWrapper>
                <LottieWrapper>
                  <Lottie animationData={LoadingAnimation} loop />
                </LottieWrapper>
              </LoadingWrapper>
            }
            {
              !isLoading &&
              sortedMembers.map((data, index) => (
                <TotalMemberInfo key={'TotalSendMember-' + index} onClick={() => onClickMemberInfo(data)}>
                  <MemberCheckBox isClicked={data.isClicked} style={{ marginRight: '10px' }}>
                    {
                      data.isClicked &&
                      <CheckIcon />
                    }
                  </MemberCheckBox>
                  <TextWrapper marginRight={10} width={120}>
                    <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} hover='pointer'>{data.name}</Text>
                  </TextWrapper>
                  <TextWrapper marginRight={10} width={50}>
                    <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} hover='pointer'>{data.gender === 'male' ? t('memberDetail.male') : t('memberDetail.female')}</Text>
                  </TextWrapper>
                  <TextWrapper marginRight={10} width={200}>
                    <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} hover='pointer'>{data.membershipName}</Text>
                  </TextWrapper>
                  <TextWrapper marginRight={10} width={75}>
                    <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} hover='pointer'>{moment(data.startDate).format(t('calendarInput.shortDateFormat'))}</Text>
                  </TextWrapper>
                  <TextWrapper marginRight={10} width={75}>
                    <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} hover='pointer'>{moment(data.endDate).format(t('calendarInput.shortDateFormat'))}</Text>
                  </TextWrapper>
                </TotalMemberInfo>
              ))
            }
          </TotalMemberListWrapper>
        </TotalMemberBox>
        <SelectedMemberBoxWrapper>
          <SelectedTextWrapper>
            {
              languageMode === 'ko' ?
              <TextWrapperForLanguage>
                <Text fontFamily='NotoSansKR-Regular' fontSize={16} color={theme.subText} hover='default'>{t('memberDetail.pointSaveModal.currentlyPrefix')}</Text>
                <NoGapTextWrapper>
                  <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.subKey} hover='default'>{decideSelectedCountText()}</Text>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={16} color={theme.subText} hover='default'>을</Text>
                </NoGapTextWrapper>
              </TextWrapperForLanguage>
            :
              <TextWrapperForLanguage>
                <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.subKey} hover='default'>{decideSelectedCountText()}</Text>
                <Text fontFamily='NotoSansKR-Regular' fontSize={16} color={theme.subText} hover='default'>{t('memberDetail.pointSaveModal.currentlyPrefix')}</Text>
              </TextWrapperForLanguage>
            }
            <Text fontFamily='NotoSansKR-Regular' fontSize={16} color={theme.subText} hover='default'>{t('memberDetail.pointSaveModal.selectedSuffix')}</Text>
          </SelectedTextWrapper>
          <DivisionLineWrapper>
            <DivisionLine width='100%' marginTop={10} />
          </DivisionLineWrapper>
          <SelectedMemberBox>
            <SelectedMemberBoxScrollWrapper ref={SelectedScrollRef}>
              <SelectedMemberInfoWrapper>
                {
                  originalSortedMembers.map((data, index) => (
                    data.isClicked &&
                    <SelectedMemberInfo key={'SelectedSortMember-' + index}>
                      <ProfileWrapper isFirst={index === 0} isLast={originalSortedMembers.length - 1 === index}>
                        <UserProfile profile={data.profile} defaultProfile={DefaultProfile} />
                      </ProfileWrapper>
                      <InfoWrapper>
                        <TextWrapper>
                          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} hover='default'>{data.name}</Text>
                        </TextWrapper>
                        <TextWrapper>
                          <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.subText} hover='default'>{formatPhoneNumber(data.phone, data.countryCode, languageMode) || t('memberDetail.noPhone')}</Text>
                        </TextWrapper>
                      </InfoWrapper>
                      <DeleteButtonWrapper onClick={() => onClickDeleteButton(data)}>
                        <DeleteButton>
                          <DeleteIcon />
                        </DeleteButton>
                      </DeleteButtonWrapper>
                    </SelectedMemberInfo>
                  ))
                }
              </SelectedMemberInfoWrapper>
            </SelectedMemberBoxScrollWrapper>
            <CustomScrollbar
              direction='vertical'
              scrollRef={SelectedScrollRef}
              thumbSize='auto'
              thumbThickness={2}
              trackOffset={5}
              showTrackAlways={false}
              trackColor='transparent' />
          </SelectedMemberBox>
        </SelectedMemberBoxWrapper>
      </TotalMemberBoxWrapper>
      <BottomWraper>
        <PaginationWrapper>
          <PaginationButton isAvailableClick={nowPage !== 0} onClick={onClickPrevButton}>
            <LeftPaginationTriangle />
          </PaginationButton>
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} hover='default'>{(nowPage + 1) + '/' + totalPage}</Text>
          <PaginationButton isAvailableClick={(nowPage + 1) !== totalPage} onClick={onClickNextButton}>
            <RightPaginationTriangle />
          </PaginationButton>
        </PaginationWrapper>
      </BottomWraper>
    </AdditionMemberView>
  );
})

export default BulkPointMemberSelectView;

const AdditionMemberView = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 10px;
`;

const SearchInputWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  padding-right: 20px;
  
  box-sizing: border-box;
  width: 100%;
`;

const ResetAndSortingButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  padding-right: 20px;
  
  box-sizing: border-box;
  width: 100%;
  height: 52px;
`;

const SortingButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 14px;
`;

const FilterResetButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 0 10px;

  height: 31px;

  border: 1px solid ${props => props.theme.buttonFill};
  border-radius: 5px;
  background-color: ${props => props.theme.buttonFill};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const TotalMemberBoxWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  padding: 6px 0;

  box-sizing: border-box;
  width: 100%;
  height: 404px;
`;

const TotalMemberBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 598px;
  height: 100%;
`;

const TotalMemberColumnWrppaer = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 100%;
`;

const MemberCheckBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  flex-shrink: 0;

  width: 16px;
  height: 16px;

  border: ${props => props.isClicked ? `1px solid ${props.theme.subKey}` : `1px solid ${props.theme.subText}`};
  border-radius: 5px;
  background-color: ${props => props.isClicked ? props.theme.subKey : props.theme.subBackground};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }

  transition: all 0.3s ease-in-out;
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  margin-right: ${props => props.marginRight}px;

  width: ${props => props.width ? `${props.width}px` : '100%'};
  min-width: 0;
  height: 20px;

  overflow: hidden;
`;

const AscDescButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  margin-right: ${props => props.marginRight}px;

  width: ${props => props.width}px;
  height: 20px;

  overflow: hidden;
`;

const SortingButton = styled(SortingButtonSvg)`
  margin-left: 4px;
  flex-shrink: 0;

  width: 20px;
  height: 20px;

  transform: ${props => props.$sort === 'asc' ? 'rotate(180deg)' : 'none'};

  & rect {
    fill: ${props => props.$sort !== 'normal' ? props.theme.subKey : props.theme.buttonFill3};
    transition: fill 0.3s ease-in-out;
  }

  & path:first-of-type {
    fill: ${props => props.$sort !== 'normal' ? props.theme.subBackground : props.theme.white};
    transition: fill 0.3s ease-in-out;
  }

  & path:last-of-type {
    fill: ${props => props.$sort !== 'normal' ? props.theme.subBackground : props.theme.white};
    transition: fill 0.3s ease-in-out;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const TotalMemberListWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  margin-top: 15px;

  width: 100%;
  min-height: 360px;
  max-height: 360px;

  gap: 5px;
`;

const LoadingWrapper = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 100%;
`;

const LottieWrapper = styled.div`
  width: 100px;
  height: 100px;
`;

const TotalMemberInfo = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 100%;
  height: 35px;

  border-radius: 10px;
  background-color: ${props => props.theme.subBackground};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 8px;
`;

const PaginationButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 20px;
  height: 20px;

  border-radius: 4px;
  background-color: ${props => props.isAvailableClick ? props.theme.subKey : props.theme.buttonFill3};

  transition: all 0.3s ease-in-out;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const LeftPaginationTriangle = styled.div`
  margin-left: -1.5px;

  width: 0;
  height: 0;
  border-style: solid;
  border-width: 5px 7px 5px 0px;
  border-color: transparent ${props => props.theme.white} transparent transparent;
`;

const RightPaginationTriangle = styled.div`
  margin-right: -1px;

  width: 0;
  height: 0;
  border-style: solid;
  border-width: 5px 0px 5px 7px;
  border-color: transparent transparent transparent ${props => props.theme.white};
`;

const SelectedMemberBoxWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  margin-left: 20px;

  width: 241px;
  flex-shrink: 0;
`;

const SelectedTextWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 4px;
`;

const TextWrapperForLanguage = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 4px;
`

const NoGapTextWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

const SelectedMemberBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: stretch;

  margin-top: 10px;
  
  width: 241px;
`;

const SelectedMemberBoxScrollWrapper = styled.div`
  display: flex;
  flex-direction: column;

  box-sizing: border-box;
  flex: 1;
  min-width: 0;
  max-height: 352px;

  border-radius: 10px;
  box-shadow: 0px 4px 30px 0px rgba(0, 0, 0, 0.05);

  overflow-x: hidden;
  overflow-y: scroll;

  scrollbar-width: none;

  &::-webkit-scrollbar {
    width: 0;
  }
`

const SelectedMemberInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  background-color: ${props => props.theme.subBackground};
`;

const SelectedMemberInfo = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 100%;
  height: 44px;
`;

const ProfileWrapper = styled.div`
  flex-shrink: 0;
  width: 44px;
  height: 44px;

  border-top-left-radius: ${props => props.isFirst ? `4px` : `0px`};
  border-bottom-left-radius: ${props => props.isLast ? `4px` : `0px`};

  overflow: hidden;

  img {
    object-fit: cover;
  }
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;

  margin-left: 10px;

  width: 140px;
  height: 100%;
`;

const DivisionLineWrapper = styled.div`
  padding: 0 20px;

  box-sizing: border-box;
  width: 100%;
`;

const DivisionLine = styled.div`
  display: flex;
  flex-direction: column;

  margin-top: ${props => props.marginTop}px;

  width: ${props => props.width};
  height: 1px;

  border-radius: 2px;
  background-color: ${props => props.theme.line};
`;

const Dday = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 80px;
  height: 20px;

  border-radius: 10px;
  background-color: ${props => props.theme.primary};

  font-family: NotoSansKR-Regular;
  font-size: 12px;
  white-space: nowrap;

  color: ${props => props.theme.subBackground};

  margin: 0 0 0 0;
`;

const DeleteButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  margin-left: 5px;

  min-width: 28px;
  min-height: 28px;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const DeleteButton = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  transform: rotate(45deg);
`;

const DeleteIcon = styled(PlusSvg)`
  width: 18px;
  height: 18px;

  & path {
    fill: ${props => props.theme.line};
  }
`;

const Stick = styled.div`
  position: absolute;
  width: ${props => props.width}px;
  height: ${props => props.height}px;

  border-radius: ${props => props.radius}px;
  background-color: ${props => props.backgroundColor};
`;

const CheckIcon = styled(CheckSvg)`
  width: 10px;
  height: auto;

  & path {
    fill: ${props => props.theme.subBackground};
  }
`;

const BottomWraper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-end;

  width: 100%;
  height: 30px;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: ${props => props.hover};
    }
  }
`;
